import {IConditionalVisibility} from '../../../pages/esgManager/interface/ICommon';
import {inputType} from '../../../pages/esgManager/interface/inputType';
import {FieldConditions} from '../interface/form-conditions';
import {
  FixedRowField,
  HeadingOutput,
  RowDatePickerField,
  RowFileUploadField,
  RowNumberField,
  RowRadioGroupField,
  RowSelectField,
  RowWysiwygField,
  FieldLabelHeading,
  SelectField,
  SelectOption,
  TableFixedRowOutput,
  TableFormOutput,
  TableRowFormField,
  TableOutput,
  SelectWithLabelOption,
  DisabledOnCondition,
  RequiredOnCondition
} from '../interface/table.interface';

export const createTableQuestion = (
  fieldName: string,
  headingTitle: string,
  headingTooltip: string,
  tableFields: TableRowFormField[],
  allowMultipleRows?: boolean,
  relatedTableKey?: string,
  dependsOn?: string,
  showWhen?: string | string[],
  conditions?: FieldConditions
): TableFormOutput => {
  const hasTooltip = typeof headingTooltip === 'string';
  const tooltip = hasTooltip ? headingTooltip : undefined;

  const tableField: TableOutput = {
    fieldType: inputType.ADD_NEW_TO_TABLE,
    fieldName: fieldName,
    controlHeading: {
      fieldType: inputType.HEADING,
      heading: headingTitle,
      tooltip: tooltip
    },
    conditions: conditions,
    allowMultipleRows: allowMultipleRows ?? false,
    ...(relatedTableKey && {relatedTableKey}),
    ...(dependsOn && {dependsOn}),
    ...(showWhen && {showWhen}),
    formList: {
      formFields: tableFields
    }
  };

  return {
    field: [tableField]
  };
};

export const createTableWithFixedRowsQuestion = (
  fieldName: string,
  headingTitle: string,
  headingTooltip: string,
  scopeColumnTitle: string,
  fixedRows: FixedRowField[],
  tableFields: TableRowFormField[],
  multipleConditionalVisibility?: IConditionalVisibility[],
  conditions?: FieldConditions
): TableFormOutput => {
  const hasTooltip = typeof headingTooltip === 'string';
  const tooltip = hasTooltip ? headingTooltip : undefined;

  const tableField: TableFixedRowOutput = {
    fieldType: inputType.ADD_FIXED_ROW_TO_TABLE,
    controlHeading: {
      fieldType: inputType.HEADING,
      heading: headingTitle,
      tooltip: tooltip
    },
    conditions: conditions,
    fieldName: fieldName,
    fixedRows: fixedRows,
    scopeColumnTitle: scopeColumnTitle,
    formList: {
      formFields: tableFields
    },
    multipleConditionalVisibility
  };

  return {
    field: [tableField]
  };
};

export const createHeading = (
  headingTitle: string,
  headingTooltip: string
): HeadingOutput => {
  const hasTooltip = typeof headingTooltip === 'string';
  const tooltip = hasTooltip ? headingTooltip : undefined;

  const headingField: HeadingOutput = {
    fieldType: inputType.HEADING,
    heading: `${headingTitle}`,
    tooltip
  };

  return headingField;
};

export const createSelectField = (
  fieldName: string,
  fieldLabel: string,
  options: SelectOption[],
  heading?: FieldLabelHeading
): SelectField => {
  return {
    fieldName: fieldName,
    fieldLabel: fieldLabel,
    options: options,
    fieldType: inputType.SELECT,
    ...(heading !== undefined ? {heading} : {})
  };
};

export const createRowMultiSelectField = (
  fieldName: string,
  fieldLabel: string,
  options: SelectOption[] | SelectWithLabelOption[],
  colSpan?: boolean | DisabledOnCondition,
  disabledOn?: DisabledOnCondition,
  requiredOn?: RequiredOnCondition
): RowSelectField => {
  // Handle case where disabledOn is passed as 4th parameter (for backward compatibility)
  let actualColSpan: boolean | undefined;
  let actualDisabledOn: DisabledOnCondition | undefined;

  if (
    typeof colSpan === 'object' &&
    colSpan !== null &&
    'fieldName' in colSpan
  ) {
    // disabledOn was passed as 4th parameter
    actualDisabledOn = colSpan;
    actualColSpan = true;
  } else {
    actualColSpan = colSpan ?? true;
    actualDisabledOn = disabledOn;
  }

  return {
    fieldName: fieldName,
    fieldLabel: fieldLabel,
    options: options,
    hasOtherField: false,
    colSpan: actualColSpan,
    type: inputType.MULTISELECT,
    ...(actualDisabledOn && {disabledOn: actualDisabledOn}),
    ...(requiredOn && {requiredOn: requiredOn})
  };
};

export const createRowSelectField = (
  fieldName: string,
  fieldLabel: string,
  options: SelectOption[] | SelectWithLabelOption[],
  colSpan?: boolean | DisabledOnCondition,
  disabledOn?: DisabledOnCondition,
  requiredOn?: RequiredOnCondition
): RowSelectField => {
  const hasOtherField =
    options?.some(
      (option: SelectOption | SelectWithLabelOption) =>
        option?.label?.trim() === 'Other, please specify'.trim()
    ) ?? false;

  // Handle case where disabledOn is passed as 4th parameter (for backward compatibility)
  let actualColSpan: boolean | undefined;
  let actualDisabledOn: DisabledOnCondition | undefined;

  if (
    typeof colSpan === 'object' &&
    colSpan !== null &&
    'fieldName' in colSpan
  ) {
    // disabledOn was passed as 4th parameter
    actualDisabledOn = colSpan;
    actualColSpan = true;
  } else {
    actualColSpan = colSpan ?? true;
    actualDisabledOn = disabledOn;
  }

  return {
    fieldName: fieldName,
    fieldLabel: fieldLabel,
    options: options,
    hasOtherField: hasOtherField,
    colSpan: actualColSpan,
    type: hasOtherField
      ? inputType.SELECT_WITH_OTHER_TEXTBOX
      : inputType.SELECT,
    ...(actualDisabledOn && {disabledOn: actualDisabledOn}),
    ...(requiredOn && {requiredOn: requiredOn})
  };
};

export const createRowRadioGroupField = (
  fieldName: string,
  fieldLabel: string,
  options: string[],
  colSpan?: boolean
): RowRadioGroupField => {
  return {
    fieldName: fieldName,
    fieldLabel: fieldLabel,
    options: options,
    colSpan: colSpan ?? true,
    type: inputType.RADIOGROUP
  };
};

export const createRowNumberField = (
  fieldName: string,
  fieldLabel: string,
  roundTo?: number,
  colSpan?: boolean,
  disabledOn?: DisabledOnCondition,
  requiredOn?: RequiredOnCondition
): RowNumberField => {
  return {
    fieldName: fieldName,
    fieldLabel: fieldLabel,
    roundTo: roundTo ?? 2,
    colSpan: colSpan ?? true,
    type: inputType.NUMBER,
    ...(disabledOn && {disabledOn: disabledOn}),
    ...(requiredOn && {requiredOn: requiredOn})
  };
};

export const createRowDatePickerField = (
  fieldName: string,
  fieldLabel: string,
  dateFormat?: string,
  colSpan?: boolean,
  disabledOn?: DisabledOnCondition,
  requiredOn?: RequiredOnCondition
): RowDatePickerField => {
  return {
    fieldName: fieldName,
    fieldLabel: fieldLabel,
    dateFormat: dateFormat ?? 'DD-MM-YYYY',
    colSpan: colSpan ?? true,
    type: inputType.DATEPICKER,
    ...(disabledOn && {disabledOn: disabledOn}),
    ...(requiredOn && {requiredOn: requiredOn})
  };
};

export const createRowFileUploadField = (
  fieldName: string,
  fieldLabel: string,
  colSpan?: boolean,
  disabledOn?: DisabledOnCondition,
  requiredOn?: RequiredOnCondition
): RowFileUploadField => {
  return {
    fieldName: fieldName,
    fieldLabel: fieldLabel,
    colSpan: colSpan ?? true,
    type: inputType.FILEUPLOAD,
    ...(disabledOn && {disabledOn: disabledOn}),
    ...(requiredOn && {requiredOn: requiredOn})
  };
};

export const createRowWysiwygField = (
  fieldName: string,
  fieldLabel: string,
  textLimit?: number,
  placeholder?: string,
  colSpan?: boolean,
  disabledOn?: DisabledOnCondition,
  requiredOn?: RequiredOnCondition
): RowWysiwygField => {
  return {
    fieldName: fieldName,
    fieldLabel: fieldLabel,
    textLimit: textLimit ?? 2500,
    placeholder: placeholder ?? fieldLabel,
    colSpan: colSpan ?? true,
    type: inputType.WYSIWYG,
    ...(disabledOn && {disabledOn: disabledOn}),
    ...(requiredOn && {requiredOn: requiredOn})
  };
};
